// @djsem/analysis-contract — Story Show Timeline (Prompt 06A).
//
// Converts analysis package data into a performance-oriented timeline for Prep Workspace,
// Chapter Editor, Set Builder, and (later) Rehearsal / Live Conductor INPUT. Prep-side
// only — never a renderer or live-runtime component. No lyrics are invented; fallback
// timelines are explicit and low-confidence.

import {
  vArray, vNumber, vObject, vString, vUnion, vRefine, type Validator,
  type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult,
} from "@djsem/shared";
import type { DJSEMChapter, DJSEMChapterType } from "@djsem/shared";
import type { DJSEMCuePoint } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** The narrative/structural role of a single story timeline segment. */
export type DJSEMStoryTimelineSegmentType =
  | "intro" | "verse" | "pre_chorus" | "chorus" | "post_chorus" | "build" | "drop"
  | "breakdown" | "bridge" | "outro" | "instrumental" | "transition" | "unknown" | "custom";

/** A segment's position anchor, expressed in whichever of time/bar/beat is available. */
export interface DJSEMStoryTimelineAnchor {
  startTimeMs?: number; endTimeMs?: number;
  startBar?: number; endBar?: number;
  startBeat?: number; endBeat?: number;
}

/** A single segment of a performance-oriented story show timeline. */
export interface DJSEMStoryTimelineSegment {
  id: string;
  type: DJSEMStoryTimelineSegmentType;
  title: string;
  description?: string;
  anchor: DJSEMStoryTimelineAnchor;
  energy: number;
  intensity: number;
  semanticDomains: string[];
  semanticCategories: string[];
  symbols: string[];
  suggestedCueIntents: string[];
  suggestedVisualSceneIds: string[];
  suggestedLightingSceneIds: string[];
  confidence: number;
  source: "analysis" | "transcript" | "manual" | "fallback" | "estimated";
  warnings: string[];
}

/** A full performance-oriented story show timeline derived from an analysis package. */
export interface DJSEMStoryShowTimeline {
  id: string;
  preparationTrackId: string;
  packageId: string;
  timelineVersion: string;
  segments: DJSEMStoryTimelineSegment[];
  warnings: string[];
  confidence: number;
  sourceSummary: {
    hasLyrics: boolean; hasTranscript: boolean; hasBeatGrid: boolean;
    hasBarData: boolean; fallbackUsed: boolean;
  };
  createdAt: string;
}

/** A raw semantic-analysis timeline segment, as produced upstream before normalization. */
export interface SemanticTimelineSegmentInput {
  id?: string;
  type?: string;
  label?: string;
  startTimeMs?: number; endTimeMs?: number; startBar?: number; endBar?: number;
  energy?: number; intensity?: number;
  semanticDomains?: string[]; semanticCategories?: string[]; symbols?: string[];
  confidence?: number;
}

/** Input for {@link StoryShowTimelineService.buildTimelineFromAnalysisPackage}. */
export interface BuildStoryTimelineInput {
  preparationTrackId: string;
  packageId: string;
  createdAt?: string;
  durationSeconds?: number;
  semanticSegments?: SemanticTimelineSegmentInput[];
  hasLyrics?: boolean;
  hasTranscript?: boolean;
  hasBeatGrid?: boolean;
}

/** Input for {@link StoryShowTimelineService.buildFallbackTimeline}. */
export interface BuildFallbackTimelineInput {
  preparationTrackId: string;
  packageId: string;
  durationSeconds?: number;
  createdAt?: string;
}

/** Input for {@link StoryShowTimelineService.deriveSegmentsFromTranscript}. */
export interface DeriveTranscriptSegmentsInput {
  transcriptSegments: Array<{ id: string; startTimeMs?: number; endTimeMs?: number; confidence?: number }>;
}

/** Input for {@link StoryShowTimelineService.deriveSegmentsFromSemanticTimeline}. */
export interface DeriveSegmentsInput {
  segments: SemanticTimelineSegmentInput[];
}

// ---- validator --------------------------------------------------------------

const vSegType: Validator = vUnion(...(["intro", "verse", "pre_chorus", "chorus", "post_chorus",
  "build", "drop", "breakdown", "bridge", "outro", "instrumental", "transition", "unknown", "custom"] as const)
  .map((s) => vString({ enum: [s] })));

/** Structural validator for a single {@link DJSEMStoryTimelineSegment}. */
export const vStoryTimelineSegment: Validator = vObject(
  {
    id: vString({ min: 1 }), type: vSegType, title: vString({ min: 1 }),
    anchor: vObject({}, { startTimeMs: vNumber({ min: 0 }), endTimeMs: vNumber({ min: 0 }), startBar: vNumber({ min: 0 }), endBar: vNumber({ min: 0 }), startBeat: vNumber({ min: 0 }), endBeat: vNumber({ min: 0 }) }, { open: true }),
    energy: vNumber({ min: 0, max: 1 }), intensity: vNumber({ min: 0, max: 1 }),
    semanticDomains: vArray(vString({})), semanticCategories: vArray(vString({})), symbols: vArray(vString({})),
    suggestedCueIntents: vArray(vString({})), suggestedVisualSceneIds: vArray(vString({})), suggestedLightingSceneIds: vArray(vString({})),
    confidence: vNumber({ min: 0, max: 1 }),
    source: vString({ enum: ["analysis", "transcript", "manual", "fallback", "estimated"] }),
    warnings: vArray(vString({})),
  },
  { description: vString({}) },
);

/** Structural + cross-field validator for a full {@link DJSEMStoryShowTimeline}: segment shapes plus no time overlaps. */
export const vStoryShowTimeline: Validator<DJSEMStoryShowTimeline> = vRefine(
  vObject({
    id: vString({ min: 1 }), preparationTrackId: vString({ min: 1 }), packageId: vString({ min: 1 }),
    timelineVersion: vString({ min: 1 }), segments: vArray(vStoryTimelineSegment),
    warnings: vArray(vString({})), confidence: vNumber({ min: 0, max: 1 }),
    sourceSummary: vObject({ hasLyrics: (v) => okBool(v), hasTranscript: (v) => okBool(v), hasBeatGrid: (v) => okBool(v), hasBarData: (v) => okBool(v), fallbackUsed: (v) => okBool(v) }),
    createdAt: vString({ min: 1 }),
  }) as Validator<DJSEMStoryShowTimeline>,
  (tl) => {
    // No overlap on time anchors where both segments have times.
    const timed = tl.segments.filter((s) => typeof s.anchor.startTimeMs === "number" && typeof s.anchor.endTimeMs === "number")
      .map((s) => [s.anchor.startTimeMs!, s.anchor.endTimeMs!] as const)
      .sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < timed.length; i++) {
      if (timed[i][0] < timed[i - 1][1]) return "overlapping timeline segments are not allowed";
    }
    return null;
  },
);
function okBool(v: unknown): ValidationResult { return typeof v === "boolean" ? { valid: true, issues: [] } : { valid: false, issues: [{ path: "$", message: "expected boolean" }] }; }

// ---- service (class-based; hard rule 12) ------------------------------------

const clampUnit = (n: number) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));
const SEG_TO_CHAPTER: Record<DJSEMStoryTimelineSegmentType, DJSEMChapterType> = {
  intro: "opening", verse: "development", pre_chorus: "build", chorus: "peak", post_chorus: "recovery",
  build: "build", drop: "peak", breakdown: "recovery", bridge: "development", outro: "closing",
  instrumental: "development", transition: "custom", unknown: "custom", custom: "custom",
};

/**
 * Converts analysis package data into a performance-oriented story show timeline for Prep
 * Workspace, Chapter Editor, and Set Builder. Prep-side only. No lyrics are invented; fallback
 * timelines are explicit and low-confidence.
 */
export class StoryShowTimelineService {
  /**
   * Build a story timeline from an analysis package's semantic segments, or fall back to a duration-estimated timeline if none exist.
   * @param input - the track/package ids and optional semantic segments/duration/source flags.
   * @returns the constructed story show timeline.
   */
  buildTimelineFromAnalysisPackage(input: BuildStoryTimelineInput): DJSEMStoryShowTimeline {
    const createdAt = input.createdAt ?? new Date().toISOString();
    const hasSemantic = (input.semanticSegments?.length ?? 0) > 0;

    if (!hasSemantic) {
      return this.buildFallbackTimeline({ preparationTrackId: input.preparationTrackId, packageId: input.packageId, durationSeconds: input.durationSeconds, createdAt });
    }
    const segments = this.deriveSegmentsFromSemanticTimeline({ segments: input.semanticSegments! });
    const hasBar = segments.some((s) => typeof s.anchor.startBar === "number");
    const confidence = clampUnit(avg(segments.map((s) => s.confidence)));
    return {
      id: `stl_${input.packageId}`, preparationTrackId: input.preparationTrackId, packageId: input.packageId,
      timelineVersion: "1.0", segments, warnings: [], confidence,
      sourceSummary: { hasLyrics: !!input.hasLyrics, hasTranscript: !!input.hasTranscript, hasBeatGrid: !!input.hasBeatGrid, hasBarData: hasBar, fallbackUsed: false },
      createdAt,
    };
  }

  /**
   * Normalize raw semantic-analysis segments into full story timeline segments, sorted deterministically by start position.
   * @param input - the raw semantic segments to normalize.
   * @returns the normalized, sorted timeline segments.
   */
  deriveSegmentsFromSemanticTimeline(input: DeriveSegmentsInput): DJSEMStoryTimelineSegment[] {
    // Deterministic: sort by start (time then bar), stable.
    const sorted = [...input.segments].sort((a, b) =>
      (a.startTimeMs ?? a.startBar ?? 0) - (b.startTimeMs ?? b.startBar ?? 0));
    return sorted.map((s, i) => {
      const warnings: string[] = [];
      if (s.startTimeMs === undefined && s.startBar === undefined) warnings.push("Segment has no time or bar anchor.");
      return {
        id: s.id ?? `seg_${i}`,
        type: this.normalizeType(s.type),
        title: s.label ?? this.normalizeType(s.type),
        anchor: { startTimeMs: s.startTimeMs, endTimeMs: s.endTimeMs, startBar: s.startBar, endBar: s.endBar },
        energy: clampUnit(s.energy ?? 0.5),
        intensity: clampUnit(s.intensity ?? s.energy ?? 0.5),
        semanticDomains: s.semanticDomains ?? [],
        semanticCategories: s.semanticCategories ?? [],
        symbols: s.symbols ?? [],
        suggestedCueIntents: [], suggestedVisualSceneIds: [], suggestedLightingSceneIds: [],
        confidence: clampUnit(s.confidence ?? 0.6),
        source: "analysis",
        warnings,
      };
    });
  }

  /**
   * Build an honest, low-confidence fallback timeline: duration-estimated segments if a duration is known, otherwise a minimal 3-segment "unknown" timeline.
   * @param input - the track/package ids and optional known duration.
   * @returns the fallback story show timeline (fallbackUsed: true).
   */
  buildFallbackTimeline(input: BuildFallbackTimelineInput): DJSEMStoryShowTimeline {
    const createdAt = input.createdAt ?? new Date().toISOString();
    const dur = input.durationSeconds;
    let segments: DJSEMStoryTimelineSegment[];
    let warnings: string[];

    if (typeof dur === "number" && dur > 0) {
      const parts: Array<[DJSEMStoryTimelineSegmentType, number]> = [
        ["intro", 0.1], ["build", 0.25], ["drop", 0.3], ["breakdown", 0.2], ["outro", 0.15],
      ];
      // deterministic estimated time ranges from duration
      let acc = 0;
      const totalMs = dur * 1000;
      segments = parts.map(([type, frac], i) => {
        const start = Math.round(acc * totalMs); acc += frac;
        const end = Math.round(acc * totalMs);
        return this.fallbackSeg(`fb_${i}`, type, cap(type), start, end, 0.4);
      });
      warnings = ["Fallback timeline estimated from duration only; review before use."];
    } else {
      segments = [
        this.fallbackSeg("fb_start", "unknown", "unknown_start", undefined, undefined, 0.2),
        this.fallbackSeg("fb_body", "unknown", "unknown_body", undefined, undefined, 0.2),
        this.fallbackSeg("fb_end", "unknown", "unknown_end", undefined, undefined, 0.2),
      ];
      warnings = ["No duration available; minimal unknown timeline. Needs review."];
    }
    return {
      id: `stl_${input.packageId}`, preparationTrackId: input.preparationTrackId, packageId: input.packageId,
      timelineVersion: "1.0", segments, warnings,
      confidence: typeof dur === "number" && dur > 0 ? 0.4 : 0.2,
      sourceSummary: { hasLyrics: false, hasTranscript: false, hasBeatGrid: false, hasBarData: false, fallbackUsed: true },
      createdAt,
    };
  }

  /**
   * Convert a story timeline's segments into canonical DJSEMChapter records for the Chapter Editor.
   * @param timeline - the story timeline to convert.
   * @returns the derived chapters.
   */
  deriveChaptersFromTimeline(timeline: DJSEMStoryShowTimeline): DJSEMChapter[] {
    return timeline.segments.map((s, i) => ({
      id: `ch_${s.id}`,
      title: s.title,
      chapterType: SEG_TO_CHAPTER[s.type],
      startBar: s.anchor.startBar,
      endBar: s.anchor.endBar,
      startTimeMs: s.anchor.startTimeMs,
      endTimeMs: s.anchor.endTimeMs,
      energyStart: clampUnit(s.energy),
      energyEnd: clampUnit(i + 1 < timeline.segments.length ? timeline.segments[i + 1].energy : s.energy),
      semanticIntentIds: s.semanticCategories,
      visualSceneIds: s.suggestedVisualSceneIds,
      lightingSceneIds: s.suggestedLightingSceneIds,
      confidence: clampUnit(s.confidence),
      source: s.source === "transcript" || s.source === "estimated" ? "estimated" : (s.source as DJSEMChapter["source"]),
    }));
  }

  /**
   * Validate a (untyped) value as a well-formed story show timeline.
   * @param timeline - the value to validate.
   * @returns validation result.
   */
  validateTimeline(timeline: unknown): DJSEMValidationResult {
    return toDJSEMValidationResult(vStoryShowTimeline(timeline), "story_timeline");
  }

  /**
   * Derive timeline segments from transcript phrase/segment boundaries (hints only — never
   * invents lyrics; keeps references by segment id, not text, for public safety).
   * @param input - the transcript segments (with timing) to derive from.
   * @returns the derived timeline segments, typed "unknown" pending review.
   */
  deriveSegmentsFromTranscript(input: DeriveTranscriptSegmentsInput): DJSEMStoryTimelineSegment[] {
    return (input.transcriptSegments ?? [])
      .filter((t) => typeof t.startTimeMs === "number")
      .map((t, i) => ({
        id: `tseg_${i}`,
        type: "unknown" as DJSEMStoryTimelineSegmentType,
        title: `Phrase ${i + 1}`,
        anchor: { startTimeMs: t.startTimeMs, endTimeMs: t.endTimeMs },
        energy: 0.5, intensity: 0.5,
        semanticDomains: [], semanticCategories: [], symbols: [],
        suggestedCueIntents: [], suggestedVisualSceneIds: [], suggestedLightingSceneIds: [],
        transcriptSegmentRefs: [t.id],
        confidence: clampUnit(t.confidence ?? 0.4),
        source: "transcript" as const,
        warnings: ["Segment derived from transcript boundaries; type unknown until reviewed."],
        sourceTrace: { transcriptSegmentId: t.id },
      }));
  }

  /**
   * Suggest cue points from a story timeline by mapping segment types to cue intents (reuses the P09 cue vocabulary).
   * @param timeline - the story timeline to derive cues from.
   * @returns the suggested (unapproved) cue points.
   */
  deriveCueSuggestionsFromTimeline(timeline: DJSEMStoryShowTimeline): DJSEMCuePoint[] {
    const INTENT: Partial<Record<DJSEMStoryTimelineSegmentType, string>> = {
      intro: "intro_start", build: "build_start", drop: "drop", breakdown: "breakdown", outro: "outro_start", chorus: "impact",
    };
    const cues: DJSEMCuePoint[] = [];
    for (const s of timeline.segments) {
      const intent = INTENT[s.type];
      if (!intent || typeof s.anchor.startTimeMs !== "number") continue;
      cues.push({
        id: `cue_${s.id}`, label: `${s.title} (${intent})`, intent: intent as DJSEMCuePoint["intent"],
        timeMs: s.anchor.startTimeMs, bar: s.anchor.startBar,
        confidence: clampUnit(s.confidence), source: "analysis", approved: false,
        warnings: s.source === "fallback" ? ["Derived from fallback timeline; review before use."] : [],
      } as DJSEMCuePoint);
    }
    return cues;
  }

  // ---- private --------------------------------------------------------------
  private normalizeType(t?: string): DJSEMStoryTimelineSegmentType {
    const known = ["intro", "verse", "pre_chorus", "chorus", "post_chorus", "build", "drop", "breakdown", "bridge", "outro", "instrumental", "transition", "custom"];
    const s = (t ?? "").toLowerCase();
    return (known.includes(s) ? s : "unknown") as DJSEMStoryTimelineSegmentType;
  }
  private fallbackSeg(id: string, type: DJSEMStoryTimelineSegmentType, title: string, startMs: number | undefined, endMs: number | undefined, energy: number): DJSEMStoryTimelineSegment {
    return {
      id, type, title, anchor: { startTimeMs: startMs, endTimeMs: endMs },
      energy: clampUnit(energy), intensity: clampUnit(energy),
      semanticDomains: [], semanticCategories: [], symbols: [],
      suggestedCueIntents: [], suggestedVisualSceneIds: [], suggestedLightingSceneIds: [],
      confidence: 0.3, source: "fallback",
      warnings: startMs === undefined ? ["No anchor; estimated position."] : [],
    };
  }
}

function avg(xs: number[]): number { return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0; }
function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
